import { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import STORE from '../../../../store';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import {
  NATIONAL_CARRIERS_ACCOUNT_ID,
  NAVAJO_ACCOUNT_ID,
} from '../../../../constants';

const useBonusPrograms = () => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [bonusPrograms, setBonusPrograms] = useState<BonusTypes.BonusProgram[]>(
    [],
  );
  const [bonusPeriodsLookup, setBonusPeriodsLookup] = useState<{
    [program: string]: BonusTypes.BonusPeriod[] | undefined;
  }>({});

  useEffect(() => {
    if (
      selectedAccountId === NAVAJO_ACCOUNT_ID ||
      selectedAccountId === NATIONAL_CARRIERS_ACCOUNT_ID
    ) {
      setBonusPrograms([]);
      setBonusPeriodsLookup({});
      return;
    }

    STORE.bonus
      .getBonusProgramsRef({
        accountId: selectedAccountId,
      })
      .onSnapshot((snapshot) => {
        const newPrograms: BonusTypes.BonusProgram[] = [];
        snapshot.docs.forEach((doc) => {
          newPrograms.push(doc.data());
        });
        setBonusPrograms(newPrograms);
        setBonusPeriodsLookup({});
      });
  }, [selectedAccountId]);

  useEffect(() => {
    const listeners = bonusPrograms.map((p) => {
      return STORE.bonus
        .getBonusPeriodsRef({
          accountId: selectedAccountId,
          programDoc: p.id,
        })
        .onSnapshot((periodDocs) => {
          const newPeriods: BonusTypes.BonusPeriod[] = [];
          periodDocs.forEach((doc) => {
            newPeriods.push(doc.data());
          });

          // Archived periods (set by the go-live API on periods ending before a
          // program's go-live date) remain available in the app; individual
          // surfaces gate their own behaviour on status === 'archived'.
          const sortedPeriods = _.sortBy(newPeriods, 'startDate');

          setBonusPeriodsLookup((current) => {
            return {
              ...current,
              [p.id]: sortedPeriods,
            };
          });
        });
    });

    return () => {
      listeners.forEach((l) => l());
    };
  }, [bonusPrograms, selectedAccountId]);

  const getBonusProgram = useCallback(
    (dataset: string) => {
      return bonusPrograms.find((p) => p.dataset === dataset);
    },
    [bonusPrograms],
  );

  return { bonusPrograms, bonusPeriodsLookup, getBonusProgram };
};

export default useBonusPrograms;
